import React from 'react'
import { Select, MenuItem, InputBase } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { makeStyles, styled } from '@mui/styles'
import { newThemeObject } from '../../newTheme'
import { tokens } from '../../styles/tokens'
import { useCardHeaderStyles } from '../../styles/cards'
import CardWrapper from '../common/CardWrapper'
import { ApexOptions } from 'apexcharts'
import { Client } from '../../redux/reducers/clients'
import { Portfolio } from '../../redux/reducers/portfolio'
import ReactApexChart from 'react-apexcharts'
import { formatNumber } from '../../utils/numbersFormatter'

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    alignItems: 'stretch',
    flexGrow: 1,
  },
  summary: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: `1fr 1fr 1fr`,
  },
  summaryLabel: {
    color: tokens.color.neutral[500],
    fontWeight: 500,
    fontSize: '0.8rem',
    margin: 0,
  },
  summaryText: {
    margin: 0,
    fontSize: '0.8rem',
    fontVariantNumeric: 'tabular-nums',
  },
}))

// Select sedi v navy pasu hlavicky -> poloprusvitna bila varianta.
const SelectInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    width: '9rem',
    borderRadius: tokens.radius.md,
    position: 'relative',
    backgroundColor: 'rgba(255, 255, 255, 0.14)',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: tokens.color.white,
    padding: '7px 14px',
    transition: `box-shadow ${tokens.transition.fast}, background-color ${tokens.transition.fast}`,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.22)',
    },
    '&:focus': {
      borderRadius: tokens.radius.md,
      boxShadow: '0 0 0 3px rgba(255, 255, 255, 0.35)',
    },
  },
  '& .MuiSelect-icon': {
    color: 'rgba(255, 255, 255, 0.8)',
  },
}))

interface DonutChartProps {
  graphBarIndex: number
  handleChangeAggType: (event: React.ChangeEvent<{}>, newValue: number) => void
  optionsGraphBar: ApexOptions
  seriesGraphBar: Array<number>
  index?: number
  handleChangeIndex?: (index: number) => void
  client: Client
  portfolio: Portfolio
}

const DonutChart: React.FC<DonutChartProps> = ({
  graphBarIndex,
  handleChangeAggType,
  optionsGraphBar,
  seriesGraphBar,
  handleChangeIndex,
  index,
  client,
  portfolio,
}) => {
  const classes = useStyles()
  const cardHeader = useCardHeaderStyles()
  const { t } = useTranslation()
  const handleChange = (e: any) => {
    handleChangeAggType(e, e.target.value)
  }
  return (
    <CardWrapper className={classes.wrapper}>
      <div className={cardHeader.header}>
        <p className={cardHeader.title}>{t('ASSET_MANAGAMENT_STRUCTURE')}</p>
        <Select label="type" onChange={handleChange} defaultValue={2} input={<SelectInput />}>
          <MenuItem value={1}>{t('INSTRUMENT_TYPE')}</MenuItem>
          <MenuItem value={2}>{t('ASSET_CLASS')}</MenuItem>
          <MenuItem value={3}>{t('CURRENCY')}</MenuItem>
          <MenuItem value={4}>{t('RISK_PROFILE')}</MenuItem>
          <MenuItem value={5}>{t('HORIZONTE')}</MenuItem>
          <MenuItem value={6}>{t('SUMMARIZATION')}</MenuItem>
        </Select>
      </div>
      {graphBarIndex !== 6 && (
        <div
          style={{
            width: '100%',
          }}
        >
          <ReactApexChart options={optionsGraphBar} series={seriesGraphBar} type="donut" height={280} />
        </div>
      )}
      {graphBarIndex === 6 && (
        <div className={classes.summary}>
          <p className={classes.summaryLabel}>{t('HORIZONTE')}</p>
          <p className={classes.summaryText}>{client.investmentHorizonte}</p>
          <p className={classes.summaryText}>{formatNumber(portfolio.horizonteValue || 0)}</p>

          <p className={classes.summaryLabel}>{t('RISK')}</p>
          <p className={classes.summaryText}>{client.riskProfile}</p>
          <p className={classes.summaryText}>{formatNumber(portfolio.riskValue || 0)}</p>

          <p className={classes.summaryLabel}>{t('CATEGORY')}</p>
          <p className={classes.summaryText}>{t(client.category || '')}</p>
          <p className={classes.summaryText}>{formatNumber(calProc(portfolio.categoryValue))} %</p>

          <p className={classes.summaryLabel}>{t('EXPERIENCE')}</p>
          <p className={classes.summaryText}>{t(client.skillAndExperience || '')}</p>
          <p className={classes.summaryText}>{formatNumber(calProc(portfolio.skillAndExperienceValue))} %</p>

          <p className={classes.summaryLabel}>{t('PREFERENCE')}</p>
          <p className={classes.summaryText}>{t(client.preference || '')}</p>
          <p className={classes.summaryText}>{formatNumber(calProc(portfolio.preferenceValue))} %</p>

          <p className={classes.summaryLabel}>{t('RISK3')}</p>
          <p className={classes.summaryText}>{t(client.lossAbility || '')}</p>
          <p className={classes.summaryText}>{formatNumber(calProc(portfolio.lossAbilityValue))} %</p>
        </div>
      )}
    </CardWrapper>
  )
}

export default DonutChart

const calProc = (value: number | null) => {
  if (value === null) return 0
  return value > 100 ? 100 : value
}
